






























































































































































































































































































import { Component, Mixins } from 'vue-property-decorator';
import moment from 'moment';
import Http from '@/mixins/http';
import { Message, MessageAttachment, ChatResponse } from '@/interfaces/ai-assistant';

@Component
export default class AiAssistantDrawer extends Mixins(Http) {
  visible: boolean = false;
  inputMessage: string = '';
  messages: Message[] = [];
  attachedFiles: File[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';
  newChatConfirmVisible: boolean = false;
  private attachmentObjectUrlMap: Record<string, string> = {};
  
  private readonly THREAD_ID_KEY = 'AiAssistantThreadId';
  private readonly MESSAGES_KEY = 'AiAssistantMessages';

  get canSendMessage(): boolean {
    return !!(this.inputMessage.trim().length || this.attachedFiles.length) && !this.isLoading;
  }

  get sendButtonTooltip(): string {
    if (!this.canSendMessage && this.isLoading) {
        return this.$t('aiAssistant.tooltipWaitForResponse') as string;
    } else if (!this.canSendMessage) {
        return this.$t('aiAssistant.tooltipEnterMessageOrAttach') as string;
    } else {
        return '';
    }
  }

  showDrawer(): void {
    this.visible = true;

    this.loadMessagesFromStorage();

    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  onClose(): void {
    this.visible = false;
  }

  handleFileSelect(file: File): boolean {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      this.errorMessage = this.$t('aiAssistant.invalidFileFormat') as string;

      return false;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      this.errorMessage = this.$t('aiAssistant.fileSizeExceeded') as string;
      return false;
    }

    if (this.attachedFiles.length) {
      this.attachedFiles = [file];
    } else {
      this.attachedFiles.push(file);
    }

    this.errorMessage = '';

    return false;
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);

    this.errorMessage = '';
  }

  openNewChatConfirm(): void {
    if (this.isLoading) {
      return;
    }

    this.newChatConfirmVisible = true;
  }

  handleNewChatCancel(): void {
    this.newChatConfirmVisible = false;
  }

  handleNewChatConfirm(): void {
    this.startNewChat();

    this.newChatConfirmVisible = false;
  }

  private startNewChat(): void {
    this.cleanupAttachmentObjectUrls();
    this.messages = [];
    this.attachedFiles = [];
    this.inputMessage = '';
    this.errorMessage = '';

    localStorage.removeItem(this.THREAD_ID_KEY);
    localStorage.removeItem(this.MESSAGES_KEY);

    this.saveMessagesToStorage();

    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  async handleSendMessage(event?: KeyboardEvent): Promise<void> {
    if (event && event.shiftKey) {
      return;
    }
    
    if (event) {
      event.preventDefault();
    }

    if (!this.canSendMessage) {
      return;
    }

    const messageText = this.inputMessage.trim();
    const files = [...this.attachedFiles];

    const userMessage: Message = {
      id: this.generateMessageId(),
      role: 'user',
      content: messageText || '',
      timestamp: new Date(),
      ...(files.length ? { attachments: files.map(file => ({ name: file.name, file })) } : {}),
      status: 'sending',
      error: ''
    };

    this.messages.push(userMessage);

    this.saveMessagesToStorage();

    this.inputMessage = '';
    this.attachedFiles = [];
    
    this.$nextTick(() => {
      this.scrollToBottom();
    });

    this.errorMessage = '';

    this.isLoading = true;

    await this.processMessageSend(userMessage.id, messageText, files);
  }

  async retrySendMessage(messageId: string): Promise<void> {
    if (this.isLoading) {
      return;
    }

    const message = this.findMessageById(messageId);
    if (!message || message.role !== 'user' || message.status !== 'error') {
      return;
    }

    const files = (message.attachments || [])
      .map(attachment => attachment.file)
      .filter((file): file is File => file instanceof File);

    message.status = 'sending';
    message.error = '';
    this.errorMessage = '';

    this.saveMessagesToStorage();

    this.isLoading = true;

    await this.processMessageSend(message.id, message.content, files);
  }

  removeMessage(messageId: string): void {
    const index = this.messages.findIndex(message => message.id === messageId);
    if (index === -1) {
      return;
    }

    this.cleanupAttachmentObjectUrlsForMessage(messageId);
    this.messages.splice(index, 1);

    this.saveMessagesToStorage();
  }

  private processMessageSend(messageId: string, messageText: string, files: File[]): Promise<void> {
    return this.sendMessageToServer(messageText, files)
      .then(response => {
        if (response.threadId && response.threadId !== '0') {
          localStorage.setItem(this.THREAD_ID_KEY, response.threadId);
        }

        const message = this.findMessageById(messageId);

        if (message) {
          message.status = 'sent';
          message.error = '';
        }

        const responseText = response.answer || response.message || response.text || '';

        const assistantMessage: Message = {
          id: this.generateMessageId(),
          role: 'assistant',
          content: responseText,
          timestamp: response.timestamp ? new Date(response.timestamp) : new Date()
        };

        this.messages.push(assistantMessage);

        this.saveMessagesToStorage();

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      })
      .catch(error => {
        console.error('Error sending message:', error);

        const message = this.findMessageById(messageId);
        const errorText = this.resolveErrorMessage(error);

        if (message) {
          message.status = 'error';
          message.error = errorText;
        }

        this.saveMessagesToStorage();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private findMessageById(messageId: string): Message | undefined {
    return this.messages.find(message => message.id === messageId);
  }

  private resolveErrorMessage(_error: any): string {
    return this.$t('aiAssistant.errorSendMessage') as string;
  }

  private cleanupAttachmentObjectUrlsForMessage(messageId: string): void {
    Object.keys(this.attachmentObjectUrlMap).forEach(key => {
      if (key.startsWith(`${messageId}_`)) {
        URL.revokeObjectURL(this.attachmentObjectUrlMap[key]);

        delete this.attachmentObjectUrlMap[key];
      }
    });
  }

  async sendMessageToServer(message: string, files: File[]): Promise<ChatResponse> {
    const threadId = localStorage.getItem(this.THREAD_ID_KEY);
    
    const formData = new FormData();

    formData.append('threadId', threadId || '0');

    const textValue: string = (message && message.trim()) ? message.trim() : ' ';

    formData.append('text', textValue);

    if (files.length) {
      formData.append('file', files[0]);
    }

    const response = await this.httpSendAiAssistantMessage(formData);

    return response.data;
  }

  loadPreviousMessages(): Promise<void> {
    const threadId = localStorage.getItem(this.THREAD_ID_KEY);
    
    if (!threadId || threadId === '0' || threadId === 'null') {
      return Promise.resolve();
    }

    return this.httpGetAiAssistantMessages(threadId)
      .then(response => {
        if (response.data && response.data.messages) {
          this.messages = response.data.messages.map((msg: any) => ({
            id: msg.id || this.generateMessageId(),
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            attachments: msg.attachments,
            ...(msg.role === 'user' ? { status: 'sent' } : {}),
            error: ''
          }));
          
          this.saveMessagesToStorage();
        }
      })
      .catch(error => {
        console.error('Error loading previous messages:', error);
      });
  }

  private loadMessagesFromStorage(): void {
    try {
      this.cleanupAttachmentObjectUrls();

      const stored = localStorage.getItem(this.MESSAGES_KEY);

      if (stored) {
        const parsed = JSON.parse(stored);

        this.messages = parsed.map((msg: any) => ({
          ...msg,
          status: msg.status || (msg.role === 'user' ? 'sent' : undefined),
          error: msg.error || '',
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.warn('Failed to load messages from localStorage:', error);

      this.messages = [];
    }
  }

  private saveMessagesToStorage(): void {
    try {
      localStorage.setItem(this.MESSAGES_KEY, JSON.stringify(this.messages));
    } catch (error) {
      console.warn('Failed to save messages to localStorage:', error);
    }
  }

  private scrollToBottom(): void {
    const container = this.$refs.messagesContainer as HTMLElement;

    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  formatTime(timestamp: Date): string {
    const now = moment();
    const time = moment(timestamp);
    
    if (now.diff(time, 'days') === 0) {
      return time.format('HH:mm');
    } else if (now.diff(time, 'days') === 1) {
      return this.$t('aiAssistant.timeYesterday') as string + ', ' + time.format('HH:mm');
    } else {
      return time.format('D.MM.YYYY, HH:mm');
    }
  }

  formatMessageContent(content: string): string {
    return content.replace(/\n/g, '<br>');
  }

  getAttachmentDownloadLink(
    message: Message,
    attachment: MessageAttachment,
    index: number
  ): string | null {
    if (!attachment) {
      return null;
    }

    if (attachment.url && attachment.url.trim().length) {
      return attachment.url;
    }

    if (attachment.file instanceof File) {
      const key = this.buildAttachmentKey(message.id, index);
      const cached = this.attachmentObjectUrlMap[key];

      if (cached) {
        return cached;
      }

      const objectUrl = URL.createObjectURL(attachment.file);
      this.attachmentObjectUrlMap[key] = objectUrl;

      return objectUrl;
    }

    return null;
  }

  getAttachmentDownloadFileName(attachment: MessageAttachment): string {
    const name = this.getAttachmentDisplayName(attachment);

    if (name && name.trim().length) {
      return name;
    }

    const extension = this.extractExtension(name);

    return extension ? `file.${extension}` : 'file';
  }

  getAttachmentDisplayName(attachment?: MessageAttachment): string {
    if (!attachment) {
      return this.$t('aiAssistant.defaultFileName') as string;
    }

    if (attachment.name && attachment.name.trim().length) {
      return attachment.name;
    }

    if (attachment.file && attachment.file.name) {
      return attachment.file.name;
    }

    return this.$t('aiAssistant.defaultFileName') as string;
  }

  getAttachmentMeta(attachment?: MessageAttachment): string {
    if (!attachment) {
      return '';
    }

    const parts: string[] = [];
    const name = this.getAttachmentDisplayName(attachment);
    const extension = this.extractExtension(name);

    if (extension) {
      parts.push(extension.toUpperCase());
    }

    const size = this.resolveAttachmentSize(attachment);

    if (size !== null) {
      parts.push(this.formatFileSize(size));
    }

    return parts.join(' · ');
  }

  getUploadAttachmentMeta(file: File): string {
    const parts: string[] = [];
    const extension = this.extractExtension(file.name);

    if (extension) {
      parts.push(extension.toUpperCase());
    }

    if (file.size) {
      parts.push(this.formatFileSize(file.size));
    }

    return parts.join(' · ');
  }

  formatFileSize(size: number): string {
    const fileUnits = this.$t('aiAssistant.fileUnits') as unknown as string[];
    if (!size) {
      return '0 ' + fileUnits[0];
    }

    const units = fileUnits;
    let index = 0;
    let value = size;

    while (value >= 1024 && index < units.length - 1) {
      value /= 1024;
      index += 1;
    }

    const precision = value >= 10 || index === 0 ? 0 : 1;
    return `${value.toFixed(precision)} ${units[index]}`;
  }

  private extractExtension(filename: string): string | null {
    if (!filename) {
      return null;
    }

    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
      return null;
    }

    return filename.slice(lastDotIndex + 1);
  }

  private resolveAttachmentSize(attachment: MessageAttachment): number | null {
    if (attachment.file && attachment.file.size) {
      return attachment.file.size;
    }

    if (typeof attachment.size === 'number') {
      return attachment.size;
    }

    if (attachment.size) {
      const parsed = parseInt(attachment.size, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  private buildAttachmentKey(messageId: string, index: number): string {
    return `${messageId}_${index}`;
  }

  private cleanupAttachmentObjectUrls(): void {
    Object.values(this.attachmentObjectUrlMap).forEach(url => {
      URL.revokeObjectURL(url);
    });

    this.attachmentObjectUrlMap = {};
  }

  beforeDestroy(): void {
    this.cleanupAttachmentObjectUrls();
  }

  mounted(): void {
    this.loadMessagesFromStorage();

    const chatId = localStorage.getItem(this.THREAD_ID_KEY);

    if (chatId && chatId !== 'null') {
      this.loadPreviousMessages();
    }
  }
}
